import { HookService } from '@tokamakjs/react';
import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';

import { AuthApi, ConfirmUserResult, UserLoginResult, UserSignupFormValue } from '~/api';
import { decodeUser, setJwt, setRefreshToken } from '~/utils';

@HookService()
export class AuthService {
  constructor(private readonly _authApi: AuthApi) {}

  async signUp(data: UserSignupFormValue & { invitation_token: undefined | string }) {
    const res = await this._authApi.signUp(data);

    if (res.error) {
      return res;
    } else {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      return res;
    }
  }

  async confirmUser(token: string) {
    const res = await this._authApi.confirmUser(token);

    if (res.error) {
      return res.error;
    } else {
      if (res.data.user.confirmed) {
        setJwt(res.data.jwt);
        return ConfirmUserResult.SUCCESS;
      } else {
        return ConfirmUserResult.EMAIL_NOT_VERIFIED;
      }
    }
  }

  async getUserFromMostRecentToken() {
    return this._authApi.getUserFromMostRecentToken();
  }

  async createCompany(values: { name: string }) {
    const res = await this._authApi.createCompany(values);

    if (res.error) {
      return res;
    } else {
      setJwt(res.data.jwt);

      return res;
    }
  }

  async joinCompany(invitationToken: string) {
    const res = await this._authApi.joinCompany(invitationToken);

    if (res.error) {
      return res;
    } else {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      return res;
    }
  }

  fetchInvitation(code: string) {
    return this._authApi.fetchInvitation(code);
  }

  refreshJwt() {
    return this._authApi.refreshJwt();
  }

  getUserFromLocalToken() {
    return this._authApi.getUserFromLocalToken();
  }

  async login(values: { email: string; password: string }) {
    const res = await this._authApi.login(values);

    if (res.result === UserLoginResult.SUCCESS) {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      const user = decodeUser(res.data.jwt);

      if (user == null) {
        throw new Error();
      }

      window.i18n.changeLocale(user.locale);
    }

    return res.result;
  }

  async handleSuccessfulGoogleLogin(
    googleResponse: GoogleLoginResponse | GoogleLoginResponseOffline,
  ) {
    const res = await this._authApi.handleSuccessfulGoogleLogin(googleResponse);

    if (!res.error) {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      const user = decodeUser(res.data.jwt);

      if (user == null) {
        throw new Error();
      }

      window.i18n.changeLocale(user.locale);
    }

    return res;
  }

  async handleSuccessfulMicrosoftLogin(code: string) {
    const res = await this._authApi.handleSuccessfulMicrosoftLogin(code);

    if (!res.error) {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      const user = decodeUser(res.data.jwt);

      if (user == null) {
        throw new Error();
      }

      window.i18n.changeLocale(user.locale);
    }

    return res;
  }

  async handleSuccessfulGoogleSignup(
    googleResponse: GoogleLoginResponse | GoogleLoginResponseOffline,
    invitationToken: string | undefined,
  ) {
    const res = await this._authApi.handleSuccessfulGoogleSignup(googleResponse, invitationToken);

    if (!res.error) {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      const user = decodeUser(res.data.jwt);

      if (user == null) {
        throw new Error();
      }

      window.i18n.changeLocale(user.locale);
    }

    return res;
  }

  async handleSuccessfulMicrosoftSignup(code: string, invitationToken: string | undefined) {
    const res = await this._authApi.handleSuccessfulMicrosoftSignup(code, invitationToken);

    if (!res.error) {
      setJwt(res.data.jwt);
      setRefreshToken(res.data.refresh_token);

      const user = decodeUser(res.data.jwt);

      if (user == null) {
        throw new Error();
      }

      window.i18n.changeLocale(user.locale);
    }

    return res;
  }

  sendPasswordResetRequest(values: { email: string }) {
    return this._authApi.sendPasswordResetRequest(values);
  }

  async changePassword(values: { password: string; code: string }) {
    return this._authApi.changePassword(values);
  }
}
